/**
 * The Belegjournal: every receipt and invoice, filterable the way an accountant asks for them
 * ("all incoming invoices from March over 400 € that are still open"), sortable by column and
 * paged by the database, so a business with years of receipts loads one page at a time.
 *
 * The state lives in React and is mirrored into the URL (`lib/documents/document-list.ts`), so a filtered
 * view can be linked to - the dashboard sends people here with ?status=open.
 */
import { Download, Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  BelegNumber,
  KindBadge,
  StatusLabel,
  statusRule,
} from "@/components/documents/document-badges";
import { DocumentToolbar } from "@/components/documents/document-filters";
import { EmptyState } from "@/components/shared/empty-state";
import { ListPagination } from "@/components/shared/list-pagination";
import { SortableHead } from "@/components/shared/sortable-head";
import { UploadDialog } from "@/components/documents/upload-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCategories, useDocumentPage, useDocumentsTotal } from "@/hooks/use-documents";
import { DOCUMENT_KINDS, listDocuments } from "@/lib/api/documents";
import {
  listStateParams,
  nextSort,
  parseListState,
  sheetFilterCount,
  type DocumentFilters,
  type ListState,
  type SortKey,
} from "@/lib/documents/document-list";
import {
  buildCsv,
  documentRows,
  downloadCsv,
  DOCUMENT_COLUMNS,
  exportLabels,
} from "@/lib/export/tables";
import { formatDate, formatMoney } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

const ALL = "all";

export function DocumentsPage() {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // The state lives in React and is written into the URL by the same handler that changes it -
  // never read back from the URL (two quick changes would overwrite each other), and never from
  // an effect (it can run after a click on a row has left the page, and navigate back).
  const [state, setState] = useState<ListState>(() => parseListState(params));
  const { filters } = state;
  const update = (change: (current: ListState) => ListState) => {
    const next = change(state);
    setState(next);
    setParams(listStateParams(next), { replace: true });
  };

  const { data, isPending } = useDocumentPage(workspace?.id, state);
  const { data: total } = useDocumentsTotal(workspace?.id, filters);
  const { data: categories } = useCategories(workspace?.id);
  const documents = data?.items;

  // A page that no longer exists (documents deleted, or a stale link) falls back to the last one.
  const lastPage = data?.totalPages ?? 0;
  if (lastPage > 0 && state.page > lastPage) setState((s) => ({ ...s, page: lastPage }));

  // Any change to what the list shows starts again at page 1.
  const changeFilters = (changes: Partial<DocumentFilters>) =>
    update((s) => ({ ...s, filters: { ...s.filters, ...changes }, page: 1 }));
  const sortBy = (key: SortKey) => update((s) => ({ ...s, ...nextSort(s, key), page: 1 }));
  const filtered =
    Boolean(filters.search.trim()) || filters.statuses.length > 0 || sheetFilterCount(filters) > 0;

  async function exportCsv() {
    if (!workspace) return;
    setExporting(true);
    try {
      // Every matching document, not only the page on screen.
      const all = await listDocuments(workspace.id, filters, state.sort, state.direction);
      const header = DOCUMENT_COLUMNS.map((column) => t(`export.documents.${column}`));
      downloadCsv(
        `${workspace.name}-belege.csv`,
        buildCsv(header, documentRows(all, categories ?? [], exportLabels(t))),
      );
    } catch {
      toast.error(t("documents.exportFailed"));
    } finally {
      setExporting(false);
    }
  }

  const head = (key: SortKey, label: string, className?: string, alignEnd?: boolean) => (
    <SortableHead
      sortKey={key}
      label={label}
      sort={state.sort}
      direction={state.direction}
      onSort={sortBy}
      className={className}
      alignEnd={alignEnd}
    />
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("documents.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("documents.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => void exportCsv()}
            disabled={!data?.totalItems || exporting}
            data-testid="export-documents"
          >
            <Download /> {t("common.export")}
          </Button>
          {canWrite && (
            <Button onClick={() => setUploadOpen(true)} data-testid="upload-receipt">
              <Upload /> {t("dashboard.upload")}
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <Tabs
          value={filters.kind}
          onValueChange={(value) => changeFilters({ kind: value as DocumentFilters["kind"] })}
        >
          <TabsList>
            <TabsTrigger value={ALL}>{t("common.all")}</TabsTrigger>
            {DOCUMENT_KINDS.map((kind) => (
              <TabsTrigger key={kind} value={kind} data-testid={`filter-kind-${kind}`}>
                <span className="mr-1.5 ledger-id">{kind}</span>
                <span className="hidden sm:inline">{t(`kinds.${kind}`)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <DocumentToolbar
          workspaceId={workspace?.id}
          filters={filters}
          categories={categories ?? []}
          onChange={changeFilters}
        />
      </div>

      {isPending && <Skeleton className="h-64 w-full" aria-busy="true" />}

      {documents && documents.length === 0 && filtered && (
        <EmptyState
          title={t("documents.noMatchesTitle")}
          description={t("documents.noMatchesText")}
          testId="documents-no-matches"
        />
      )}

      {documents && documents.length === 0 && !filtered && (
        <EmptyState
          title={t("documents.emptyTitle")}
          description={t("documents.emptyText")}
          testId="documents-empty"
          action={
            canWrite && (
              <Button onClick={() => setUploadOpen(true)}>
                <Upload /> {t("dashboard.upload")}
              </Button>
            )
          }
        />
      )}

      {documents && documents.length > 0 && (
        <div className="overflow-hidden rounded-lg border">
          <Table data-testid="documents-table">
            {/* On a phone each row is a small grid - partner and amount, then Belegnummer, date
                and status - so every cell is rendered once and only placed differently. */}
            <TableHeader className="hidden sm:table-header-group">
              <TableRow>
                <TableHead className="w-16">{t("documents.kind")}</TableHead>
                {head("number", t("documents.number"))}
                {head("date", t("documents.date"))}
                {head("partner", t("documents.partner"))}
                <TableHead className="hidden lg:table-cell">{t("documents.category")}</TableHead>
                {head("gross", t("documents.gross"), "text-right", true)}
                {head("status", t("documents.status"), "text-right", true)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((document) => (
                <TableRow
                  key={document.id}
                  // The links cover each cell's text; this covers the padding between them.
                  onClick={(event) => {
                    if (!(event.target as HTMLElement).closest("a")) {
                      void navigate(`/documents/${document.id}`);
                    }
                  }}
                  className={cn(
                    "relative grid cursor-pointer grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 p-3 sm:static sm:table-row sm:p-0",
                    statusRule(document.status),
                  )}
                  data-testid="document-row"
                >
                  <TableCell className="hidden sm:table-cell">
                    <Link to={`/documents/${document.id}`} className="block">
                      <KindBadge kind={document.kind} />
                    </Link>
                  </TableCell>
                  <TableCell className="col-start-1 row-start-2 p-0 sm:p-2">
                    <Link to={`/documents/${document.id}`} className="block">
                      <BelegNumber value={document.docNumber} />
                    </Link>
                  </TableCell>
                  <TableCell className="col-start-2 row-start-2 p-0 text-xs whitespace-nowrap text-muted-foreground num sm:p-2 sm:text-sm">
                    <Link to={`/documents/${document.id}`} className="block">
                      {formatDate(document.issueDate)}
                    </Link>
                  </TableCell>
                  <TableCell className="col-span-3 col-start-1 row-start-1 min-w-0 p-0 sm:max-w-64 sm:p-2">
                    {/* On a phone this link covers the whole row, gaps included. */}
                    <Link
                      to={`/documents/${document.id}`}
                      className="block truncate font-medium after:absolute after:inset-0 sm:after:hidden"
                    >
                      {document.partnerName || t("document.noPartner")}
                    </Link>
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground lg:table-cell">
                    <Link to={`/documents/${document.id}`} className="block truncate">
                      {document.expand?.category?.name ?? "—"}
                    </Link>
                  </TableCell>
                  <TableCell className="col-start-4 row-start-1 p-0 text-right font-medium num sm:p-2">
                    <Link to={`/documents/${document.id}`} className="block">
                      {formatMoney(document.grossTotal, document.currency)}
                    </Link>
                  </TableCell>
                  <TableCell className="col-start-4 row-start-2 p-0 text-right sm:p-2">
                    <Link to={`/documents/${document.id}`} className="block">
                      <StatusLabel status={document.status} />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Count and sum of everything the filters match, not only this page. */}
          <div className="flex items-center justify-between border-t px-4 py-2 text-sm">
            <span className="text-muted-foreground" data-testid="documents-count">
              {t("documents.count", { count: data?.totalItems ?? 0 })}
            </span>
            <span className="font-medium num" data-testid="documents-total">
              {total === undefined ? "…" : formatMoney(total)}
            </span>
          </div>
        </div>
      )}

      {data && data.totalItems > 0 && (
        <ListPagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          perPage={state.size}
          onPage={(page) => {
            update((s) => ({ ...s, page }));
            window.scrollTo({ top: 0 });
          }}
          onSizeChange={(size) => update((s) => ({ ...s, size, page: 1 }))}
        />
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}
